import { useEffect, useState } from 'react';
import salesApiClient from '@/api/salesClient';
import { Card } from '@/components/ui/card';
import { DollarSign, TrendingUp, Briefcase, PieChart, Users } from 'lucide-react';

export default function RevenueAnalytics() {
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await salesApiClient.get('/analytics/revenue');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch revenue analytics:', error);
    }
  };

  if (!analytics) {
    return (
      <div className="p-6 md:p-8 lg:p-12 flex items-center justify-center">
        <p className="text-slate-400">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 space-y-8 fade-in">
      <div>
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white" data-testid="revenue-title">
          Revenue Analytics
        </h1>
        <p className="text-base text-slate-400 mt-2">Business performance & profitability metrics</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="revenue-metrics">
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm text-slate-400">Monthly Recurring Revenue</p>
              <p className="text-3xl font-bold text-white mt-2">${analytics.mrr.toLocaleString()}</p>
            </div>
            <div className="bg-green-500/20 p-3 rounded-lg">
              <DollarSign size={24} className="text-green-400" />
            </div>
          </div>
          <p className="text-xs text-slate-500">MRR from active contracts</p>
        </Card>

        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm text-slate-400">Annual Recurring Revenue</p>
              <p className="text-3xl font-bold text-white mt-2">${analytics.arr.toLocaleString()}</p>
            </div>
            <div className="bg-blue-500/20 p-3 rounded-lg">
              <TrendingUp size={24} className="text-blue-400" />
            </div>
          </div>
          <p className="text-xs text-slate-500">ARR projection</p>
        </Card>

        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm text-slate-400">Total Margin</p>
              <p className="text-3xl font-bold text-green-400 mt-2">
                {analytics.margin_percent.toFixed(1)}%
              </p>
            </div>
            <div className="bg-green-500/20 p-3 rounded-lg">
              <PieChart size={24} className="text-green-400" />
            </div>
          </div>
          <p className="text-xs text-slate-500">${analytics.total_margin.toLocaleString()} profit</p>
        </Card>

        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm text-slate-400">Active Contracts</p>
              <p className="text-3xl font-bold text-white mt-2">{analytics.total_active_contracts}</p>
            </div>
            <div className="bg-primary/20 p-3 rounded-lg">
              <Briefcase size={24} className="text-primary" />
            </div>
          </div>
          <p className="text-xs text-slate-500">Currently active</p>
        </Card>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-white mb-6">Revenue by Type</h2>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">Contract Staffing</span>
                <span className="text-lg font-bold text-white">${analytics.staffing_value.toLocaleString()}</span>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{
                    width: `${(analytics.staffing_value / analytics.total_contract_value) * 100}%`
                  }}
                ></div>
              </div>
              <p className="text-xs text-slate-500 mt-1">{analytics.staffing_contracts} contracts</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">Adhoc Projects</span>
                <span className="text-lg font-bold text-white">${analytics.project_value.toLocaleString()}</span>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{
                    width: `${(analytics.project_value / analytics.total_contract_value) * 100}%`
                  }}
                ></div>
              </div>
              <p className="text-xs text-slate-500 mt-1">{analytics.project_contracts} projects</p>
            </div>
          </div>
        </Card>

        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-white mb-6">Financial Summary</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <span className="text-sm text-slate-400">Total Contract Value</span>
              <span className="text-lg font-bold text-white">
                ${analytics.total_contract_value.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <span className="text-sm text-slate-400">Total Staff Cost</span>
              <span className="text-lg font-bold text-red-400">
                ${analytics.total_staff_cost.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm font-semibold text-white">Net Margin</span>
              <span className="text-2xl font-bold text-green-400">
                ${analytics.total_margin.toLocaleString()}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Top Clients */}
      <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-semibold text-white mb-6">Top Clients by Revenue</h2>
        <div className="space-y-3">
          {analytics.top_clients.map((client, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-4 bg-slate-800 rounded-lg hover:bg-slate-800/70 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/20 rounded-lg">
                  <Users size={20} className="text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-white">{client.contact_company}</p>
                  <p className="text-sm text-slate-400">{client.contact_name}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-white">${client.total_value.toLocaleString()}</p>
                <p className="text-xs text-slate-500">{client.contracts} contracts</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
